import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { emdFullscreenHeight } from './emd-chart-utils';

let instanceCounter = 0;

/**
 * Lifecycle and sizing shared by the EMD D3 charts.
 *
 * The app's shared charts size themselves once at draw time and never react to
 * their container changing, which is why they break when a sibling panel
 * collapses or the window is resized. Everything here exists so the subclasses
 * do not each solve that (and reduced-motion, and id collisions) differently.
 *
 * Subclasses implement `render()` and are expected to be idempotent: it is
 * called on first layout, on every input change, and on every container resize.
 */
@Directive()
export abstract class EmdChartBase implements AfterViewInit, OnChanges, OnDestroy {
  /** Unique per instance, so two charts on one page never share element ids. */
  protected readonly uid = `emd-chart-${++instanceCounter}`;

  protected width = 0;
  protected height = 0;

  private observer?: ResizeObserver;
  private resizeTimer?: number;
  private viewReady = false;

  constructor(protected readonly host: ElementRef<HTMLElement>) {}

  protected abstract render(): void;

  /** The element the ResizeObserver watches; override if it is not the host. */
  protected get measuredElement(): HTMLElement {
    return this.host.nativeElement;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.measure();
    this.render();

    this.observer = new ResizeObserver(() => this.onResize());
    this.observer.observe(this.measuredElement);

    // Entering/leaving fullscreen changes the height a chart should draw at
    // (see effectiveHeight); the ResizeObserver only fires when the WIDTH
    // happens to change too, so re-render on the event itself.
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  }

  ngOnChanges(_changes: SimpleChanges): void {
    // Before first layout there is nothing to draw into; ngAfterViewInit will
    // do the initial render. This is what makes the tabs' setTimeout wrappers
    // unnecessary — the first binding is no longer swallowed.
    if (!this.viewReady) { return; }
    this.measure();
    this.render();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.resizeTimer) { clearTimeout(this.resizeTimer); }
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
  }

  private readonly onFullscreenChange = () => {
    if (!this.viewReady) { return; }
    this.measure();
    this.render();
  };

  /**
   * The height to draw at: the design height normally; the space down to the
   * bottom of the screen when this chart's card is fullscreened. Subclasses
   * pass their `chartHeight`-style input through this in render().
   */
  protected effectiveHeight(fallback: number): number {
    return emdFullscreenHeight(this.measuredElement, fallback);
  }

  /**
   * Transition duration, or 0 when the user has asked for reduced motion.
   * Every animation in these charts routes through this rather than hardcoding
   * a duration, so the preference is honoured in one place.
   */
  protected dur(ms: number): number {
    return this.prefersReducedMotion ? 0 : ms;
  }

  protected get prefersReducedMotion(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  /** Axis tick count that thins with the available width. */
  protected tickCount(perTick = 90, max = 12): number {
    return Math.max(2, Math.min(max, Math.floor(this.width / perTick)));
  }

  private measure(): void {
    const rect = this.measuredElement.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;
  }

  private onResize(): void {
    if (this.resizeTimer) { clearTimeout(this.resizeTimer); }
    this.resizeTimer = window.setTimeout(() => {
      const previous = this.width;
      this.measure();
      // Sub-pixel jitter from scrollbars would otherwise redraw continuously.
      if (Math.abs(this.width - previous) < 1) { return; }
      this.render();
    }, 150);
  }
}
